<template>
  <div>
    <p
      class="text-dark-gray font-small"
    >
      <span class="mr-7">
        {{ `${firstPublishedDate} ${t('public')}` }}
      </span>
      <span>
        {{ `${updatedDate} ${t('update')}` }}
      </span>
    </p>
    <h1 class="case-study-title mb-7 font-weight-bold">
      {{ caseStudy.publicName }}
    </h1>
    <div>
      <case-study-client-info 
        :case-study="caseStudy"
        :industry-tag="industryTag"
        :target-areas="targetAreas"
      />
      <h2
        class="subheading mb-7"
      >
        <span class="subheading-dot" />
        <span class="subheading-text">
          {{ t('about_overall_project') }}
        </span>            
      </h2>

      <p
        v-if="caseStudy.issue"
        class="bg-gray01 px-3 py-1 mb-3 font-weight-bold"
      >
        {{ t('task_and_background') }}
      </p>
      <p
        v-if="caseStudy.issue"
        class="mb-7 px-3 word-pre-line font-middle"
        v-text="caseStudy.issue"
      />
      
      <p class="bg-gray01 px-3 py-1 mb-3 font-weight-bold">
        {{ t('only_solution') }}
      </p>
      <p class="mb-7 px-3 font-middle">
        <span 
          v-for="(name, i) in projectSolutions"
          :key="name"
        >
          {{ name + [i == projectSolutions.length - 1 ? '' : '、'] }}
        </span>
      </p>
      <p
        v-if="caseStudy.product"
        class="bg-gray01 px-3 py-1 mb-3 font-weight-bold"
      >
        {{ t('utilized_product') }}
      </p>
      <p
        v-if="caseStudy.product"
        class="mb-7 px-3 font-middle"
        v-html="autoLink(caseStudy.product)"
      >
      </p>
      <p
        v-if="caseStudy.overview"
        class="bg-gray01 px-3 py-1 mb-3 font-weight-bold"
      >
        {{ t('overview') }}
      </p>
      <p
        v-if="caseStudy.overview"
        class="mb-md-12 mb-10 px-3 word-pre-line font-middle"
        v-text="caseStudy.overview"
      />
      <h2
        class="subheading mb-7"
      >
        <span class="subheading-dot" />
        <span class="subheading-text">
          {{ `${companyName}${t('about_company_case')}` }}
        </span>            
      </h2>
      <div class="row">
        <div class="col-md-6 pr-md-4">
          <p
            v-if="caseStudy.implementingYear"
            class="bg-gray01 px-3 py-1 mb-3 font-weight-bold"
          >
            {{ t('implement_year') }}
          </p>
          <p
            v-if="caseStudy.implementingYear"
            class="mb-1 px-3 font-middle"
          >
            {{ `${caseStudy.implementingYear}${t('year')}` }}
          </p>
          <p
            v-if="caseStudy.implementingYear"
            class="mb-7 px-3 font-small text-gray04"
          >
            {{ t('implementing_year_finished') }}
          </p>
        </div>
        <div class="col-md-6 pl-md-0">
          <p
            v-if="supportPeriod"
            class="bg-gray01 px-3 py-1 mb-3 font-weight-bold"
          >
            {{ t('implementing_period') }}
          </p>
          <p
            v-if="supportPeriod"
            class="mb-7 px-3 font-middle"
          >
            {{ supportPeriod }}
          </p>
        </div>
      </div>
      
      <p class="bg-gray01 px-3 py-1 mb-3 font-weight-bold">
        {{ t('expected_point') }}
      </p>
      <div
        class="font-middle mb-7 px-3"
      >
        <span 
          v-for="(name, i) in expectedPoints"
          :key="name"
        >
          {{ name + [i == expectedPoints.length - 1 ? '' : '、'] }}
        </span>
      </div>
      <p class="bg-gray01 px-3 py-1 mb-3 font-weight-bold">
        {{ t('role') }}
      </p>
      <p
        class="mb-7 px-3 font-middle word-pre-line"
        v-text="caseStudy.role"
      />
      <p
        class="bg-gray01 px-3 py-1 mb-3 font-weight-bold"
      >
        {{ t('implementing_content') }}
      </p>
      <p
        class="font-middle word-pre-line px-3"
        :class="attachedClassContentBottom"
        v-text="caseStudy.content"
      />
      <div class="d-flex justify-content-center align-items-center">
        <a
          v-if="isShowContactCaseStudyBtn"
          class="contact-btn btn-scale-action"
          :href="contactCaseStudyPath"
        >
          <span class="mr-3">
            {{ t('contact_this_case_study') }}
          </span>
          <img
            src="../../../../assets/images/img_horizontal_arrow_white.png"
            class="btn-scale-arrow"
          >
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import "../../i18n/ja";
import { i18n } from "../../mixins/i18n";
import InlineTagsList from "../companies/InlineTagsList.vue";
import moment from "moment";
import CaseStudyClientInfo from './CaseStudyClientInfo.vue';
export default {
  components: { InlineTagsList, CaseStudyClientInfo },
  mixins: [i18n],
  props: {
    caseStudy: {
      type: Object,
      default() {
        return null;
      }
    },
    firstPublishedAt: {
      type: String,
      default: ""
    },
    companyLogo: {
      type: String,
      default: ""
    },
    companyName: {
      type: String,
      required: true
    },
    industryTag: {
      type: Array,
      default() {
        return [];
      }
    },
    targetAreas: {
      type: Array,
      default() {
        return [];
      }
    },
    projectSolutions: {
      type: Array,
      default() {
        return [];
      }
    },
    supportPeriod: {
      type: String,
      default: ""
    },
    expectedPoints: {
      type: Array,
      default() {
        return [];
      }
    },
    isShowContactCaseStudyBtn: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      defaultImage: require("../../../../assets/images/no_logo.png"),
      contactCaseStudyPath: `/case_studies/${this.caseStudy.uuid}/case_study_contacts/new`
    };
  },
  computed: {
    attachedClass() {
      return {
        "bg-gray02": this.companyLogo === ""
      };
    },
    attachedClassContentBottom() {
      return this.isShowContactCaseStudyBtn ? "mb-10" : "mb-0";
    },
    updatedDate() {
      return moment(this.caseStudy.updatedAt).format("YYYY / MM / DD");
    },
    firstPublishedDate() {
      if (this.firstPublishedAt === "") {
        return moment().format("YYYY / MM / DD");
      }
      return moment(this.firstPublishedAt, "YYYY/MM/DD HH:mm:ss").format("YYYY / MM / DD");
    }
  },
  methods: {
    autoLink(text) {
      return text.replace(/(https?:\/\/[^\s]*)/g, "<a href='$1' terget='_blank' rel='nofollow noopener noreferrer'>$1</a>");
    }
  }
};
</script>

<style lang="scss" scoped>
.implement-year {
  margin-bottom: 3px;
}

.case-study-title {
  font-size: 28px;
  @media (max-width: 767px) {
    font-size: 24px;
  }
}

.subheading-text {
  font-size: 24px;
  @media (max-width: 767px) {
    font-size: 18px;
  }
}

.font-middle {
  line-height: 28.8px;
}
</style>
